import { Button, ButtonGroup, useTheme } from "@mui/material";
import {
  CSSProperties,
  useEffect,
  useRef,
  useState,
  createRef,
} from "react";
import { useTranslation } from "react-i18next";

interface PlanButtonGroupProps {
  actualPlan: number;
  setActualPlan: (n: number) => void;
  onClick: () => void;
}

// Nuova struttura dei TAB:
// 1 = Consigliati
// 2 = Trimestrale
// 3 = Semestrale
// 4 = Annuale
const PLAN_TABS = [1, 2, 3, 4];

const PlanButtonGroup = ({
  actualPlan,
  setActualPlan,
  onClick,
}: PlanButtonGroupProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Un singolo useRef con createRef (corretto)
  const buttonRefs = useRef(PLAN_TABS.map(() => createRef<HTMLButtonElement>()));

  const [width, setWidth] = useState<number>(0);
  const [sliderLeft, setSliderLeft] = useState<number>(0);

  const sliderStyle: CSSProperties = {
    backgroundColor: theme.palette.primary.main,
    borderRadius:
      actualPlan === PLAN_TABS[0]
        ? "8px 0px 0px 8px"
        : actualPlan === PLAN_TABS[PLAN_TABS.length - 1]
        ? "0px 8px 8px 0px"
        : "0px",
    position: "absolute",
    transition: "all 0.3s ease",
    height: "100%",
    left: `${sliderLeft}px`,
    width: `${width + 1}px`,
  };

  const handleSelect = (index: number) => {
    if (actualPlan !== index) {
      setTimeout(() => setActualPlan(index), 270);
      onClick();
    }
  };

  const updateSlider = () => {
    const index = PLAN_TABS.indexOf(actualPlan);
    const currentButton = buttonRefs.current[index]?.current;

    if (currentButton) {
      setWidth(currentButton.clientWidth);
      setSliderLeft(currentButton.offsetLeft);
    }
  };

  useEffect(() => {
    updateSlider();
    window.addEventListener("resize", updateSlider);
    return () => window.removeEventListener("resize", updateSlider);
  }, [actualPlan]);

  return (
    <ButtonGroup
      variant="outlined"
      color="primary"
      sx={{
        position: "relative",
        "& .MuiButtonGroup-grouped:not(:first-of-type):not(:last-of-type)": {
          borderRight: "0px",
          borderLeft: "0px",
          borderLeftColor: "transparent",
          borderRightColor: "transparent",
        },
        "& .MuiButtonGroup-grouped:not(:first-of-type)": {
          borderLeft: "0px",
          borderLeftColor: "transparent",
        },
        "& .MuiButtonGroup-grouped:not(:last-of-type)": {
          borderRight: "0px",
          borderRightColor: "transparent",
        },
        "& .MuiButtonGroup-grouped:first-of-type": {
          borderBottomLeftRadius: "8px",
          borderTopLeftRadius: "8px",
        },
        "& .MuiButtonGroup-grouped:last-of-type": {
          borderBottomRightRadius: "8px",
          borderTopRightRadius: "8px",
        },
      }}
    >
      {/* Slider */}
      <div style={sliderStyle} />

      {/* Bottoni */}
      {PLAN_TABS.map((planValue, idx) => (
        <Button
          key={planValue}
          ref={buttonRefs.current[idx]}
          variant="outlined"
          onClick={() => handleSelect(planValue)}
          sx={{
            zIndex: 1,
            color:
              actualPlan === planValue
                ? "white"
                : theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            "&:hover": {
              background: "#ffffff33",
            },
          }}
        >
          {t(`profile.plan-${planValue}`)}
        </Button>
      ))}
    </ButtonGroup>
  );
};

export default PlanButtonGroup;
